import { zodResolver } from "@hookform/resolvers/zod";
import { NcForm, NcFormDateTimeField } from "@noted/noted-components";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { restPutter } from "~/api/fetchers";
import { PrimhdCodeQueryResponse } from "~/api/primhd";
import { PrimhdActivityRecord } from "~/api/primhd/types";
import RecordDetailsPanel from "~/app/primhd/records/record-forms/record-details-panel";
import { useToast } from "~/components/alerts/alerts";
import { useI18n } from "~/hooks/use-i18n";

import PrimhdCodeComboBox from "../../primhd-code-combo-box";

type UpdateApi = Pick<
  PrimhdActivityRecord,
  "id" | "referralId" | "activityType" | "activitySetting" | "startDate" | "endDate"
>;

interface ActivityFormProps {
  record: PrimhdActivityRecord;
  formId: string;
  codes: PrimhdCodeQueryResponse;
  onRecordUpdate: () => void;
}

const baseFormSchema = (t: (arg: string) => string) =>
  z
    .object({
      startDate: z.date(),
      endDate: z.date(),
      activityType: z.string(),
      activitySetting: z.string(),
    })
    .refine(
      ({ startDate, endDate }) => {
        if (!endDate) {
          return true;
        }
        return startDate < endDate;
      },
      {
        path: ["endDate"],
        message: t("primhd:records.edit.date_range_error"),
      }
    );

export const ActivityForm = ({ formId, record, codes, onRecordUpdate }: ActivityFormProps) => {
  const { t } = useI18n("primhd");
  const { enqueueError } = useToast();

  const { mutate: updateRecord } = useMutation({
    mutationFn: (body: UpdateApi) => restPutter(`/v1/primhd/records/activity/${record.id}`, body),
    onSuccess() {
      onRecordUpdate();
    },
    onError(error) {
      const errorMessage =
        error.status === 400 && error.message
          ? error.message
          : t("primhd:records.edit.update_error");
      enqueueError(errorMessage);
    },
  });

  const formSchema = baseFormSchema(t);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      activityType: record.activityType,
      activitySetting: record.activitySetting,
      startDate: record.startDate ? new Date(record.startDate) : undefined,
      endDate: record.endDate ? new Date(record.endDate) : undefined,
    },
  });

  const onSubmit = form.handleSubmit(data => {
    if (!data) return;

    const { startDate, endDate, activityType, activitySetting } = data;

    updateRecord({
      id: record.id,
      referralId: record.referralId,
      activityType: activityType,
      activitySetting: activitySetting,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    });
  });

  return (
    <RecordDetailsPanel>
      <NcForm form={form} onSubmit={onSubmit} id={formId}>
        <NcForm.Row>
          <PrimhdCodeComboBox
            inputWidth="full"
            className="grow"
            label={t("primhd:records.edit.activity_record.type_label")}
            name="activityType"
            codes={codes["ACTIVITY_TYPE"]}
            isRequired
          />
          <PrimhdCodeComboBox
            inputWidth="full"
            className="grow"
            label={t("primhd:records.edit.activity_record.setting_label")}
            name="activitySetting"
            codes={codes["ACTIVITY_SETTING"]}
            isRequired
          />
        </NcForm.Row>
        <NcForm.Row>
          <NcFormDateTimeField
            inputWidth="full"
            className="grow"
            label={t("primhd:records.edit.activity_record.start_date_label")}
            name="startDate"
            showPicker
            isRequired
          />
          <NcFormDateTimeField
            inputWidth="full"
            className="grow"
            label={t("primhd:records.edit.activity_record.end_date_label")}
            name="endDate"
            showPicker
            isRequired
          />
        </NcForm.Row>
      </NcForm>
    </RecordDetailsPanel>
  );
};
